import { GenericForm } from '@neo/blocks/forms/generic-form'
import type { CompProps, GenericFieldConfig } from '@neo/blocks/forms/types'
import { Button } from '@neo/primitives/button'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { useNodes } from '@xyflow/react'
import { useCallback, useMemo } from 'react'
import { EnhancedTextArea } from 'v2/shared/components/enhanced-textarea'
import { FormSubmitButton } from 'v2/shared/components/form-submit-button'
import { z } from 'zod'
import { createNodeVarValidator, findInvalidNodeVarRefs } from '../../config/validation.config'
import { useFlowStore } from '../../store'
import { FlowNodeTypes, type GenericNode, type SystemPrompt } from '../../types'
import { getAllNodeVarsFromNode } from '../../utils'
import { isAiAgentV2Type } from '../../utils/ai-agent-v29'
import { FLOW_PANEL_SECTION_TYPOGRAPHY } from '../panel-typography'

type SystemPromptFormValues = SystemPrompt

export const SystemPromptSidebar = () => {
  const nodes = useNodes<GenericNode>()
  const unselectNode = useFlowStore((state) => state.unselectNode)
  const systemPrompt = useFlowStore((state) => state.systemPrompt)
  const setSystemPrompt = useFlowStore((state) => state.setSystemPrompt)
  const setSidebarFormInstance = useFlowStore((state) => state.setSidebarFormInstance)

  // Get all AI nodes for the multi-select options
  const aiNodeOptions = useMemo(() => {
    return nodes
      .filter((node) => isAiAgentV2Type(node.type))
      .map((node) => ({
        label: node.data?.config?.name || node.id,
        value: node.id,
      }))
  }, [nodes])

  // Global Prompt is prepended to every targeted AI node at runtime; only Start
  // node vars are guaranteed to exist for all of them.
  const startNodeVars = useMemo(() => {
    const startNode = nodes.find((node) => node.type === FlowNodeTypes.start)
    return startNode ? getAllNodeVarsFromNode(startNode) : []
  }, [nodes])

  // Create form config with dynamic AI node options
  const formConfig = useMemo<GenericFieldConfig<SystemPrompt>>(
    () => [
      {
        name: 'prompt' as const,
        label: 'Prompt',
        zodValidation: z
          .string()
          .min(1, 'Prompt is required')
          .refine(createNodeVarValidator(startNodeVars), (val) => ({
            message: `Only Start node variables are allowed in Global Prompt. Invalid: ${findInvalidNodeVarRefs(val, startNodeVars).join(', ')}`,
          })),
        type: 'custom' as const,
        component: ({ value, onChange, ...props }: CompProps<SystemPromptFormValues>) => (
          <EnhancedTextArea
            value={value as string}
            onChange={onChange}
            {...props}
            showFileUpload={false}
            className="max-h-[calc(100vh-370px)] overflow-y-auto"
          />
        ),
        eventDataExtractor: (e: string) => e,
        inputProps: {
          placeholder:
            'ex: prioritise premium customers, hand over to agent if sentiment is negative, convert 24hr format to 12hr format',
          rows: 6,
        },
        className: 'col-span-full',
      },
      {
        type: 'slot' as const,
        name: 'ai_nodes_slot',
        component: (
          <div className="text-s-500 text-secondary">
            Select nodes that can access this shared node
          </div>
        ),
      },
      {
        name: 'all_nodes_enabled' as const,
        label: 'All AI conversation nodes',
        componentType: 'checkbox' as const,
        zodValidation: z.boolean().default(false),
        inputProps: {
          'data-testid': 'checkbox-select-all-ai-nodes',
        },
        className: '-mt-1',
      },
      {
        name: 'ai_nodes' as const,
        label: '',
        componentType: 'select' as const,
        zodValidation: z.array(z.string()).default([]),
        onChangeListenTo: ['all_nodes_enabled'],
        visible: (values: SystemPromptFormValues) => !values.all_nodes_enabled,
        inputProps: {
          placeholder: 'Select AI nodes to apply this prompt to...',
          options: aiNodeOptions,
          multiple: true,
          'data-testid': 'select-ai-nodes',
        },
        className: 'col-span-full',
      },
    ],
    [aiNodeOptions, startNodeVars],
  )

  const handleSubmit = useCallback(
    (values: SystemPromptFormValues) => {
      const newSystemPrompt: SystemPrompt = {
        prompt: values.prompt,
        all_nodes_enabled: values.all_nodes_enabled,
        ai_nodes: values.all_nodes_enabled
          ? aiNodeOptions.map((option) => option.value)
          : values.ai_nodes,
      }

      // Update store immediately
      setSystemPrompt(newSystemPrompt)

      // Update flow data for persistence
      unselectNode(true)
    },
    [unselectNode, setSystemPrompt, aiNodeOptions],
  )

  const handleCancel = useCallback(() => {
    unselectNode()
  }, [unselectNode])

  // Get current values from store
  const defaultValues: SystemPromptFormValues = useMemo(
    () => ({
      prompt: systemPrompt?.prompt || '',
      all_nodes_enabled: systemPrompt?.all_nodes_enabled ?? true,
      ai_nodes: systemPrompt?.ai_nodes || [],
    }),
    [systemPrompt],
  )

  return (
    <div className="w-[500px] max-w-full min-h-0 flex flex-col font-inter duration-300 ease-in-out py-2">
      <div className="pb-4 font-sora text-h4-500 text-foreground px-4">
        <div className="flex items-center gap-2 mt-2">
          <span>Global Prompt</span>
        </div>
      </div>
      <ScrollArea className="min-h-0 flex-1">
        <div className={`flex-grow px-4 ${FLOW_PANEL_SECTION_TYPOGRAPHY}`}>
          <div className="mb-4">
            <p className="text-p-400 text-secondary">
              Enter a prompt that will be added to the beginning of all nodes. This serves as
              general context for the agent
            </p>
          </div>

          <GenericForm
            id="system-prompt-form"
            fields={formConfig}
            onSubmit={handleSubmit}
            onMount={(formInstance) => {
              setSidebarFormInstance(formInstance)
            }}
            defaultValues={defaultValues}
            submitText="Save Global Prompt"
            className="gap-4"
            includeSubmitButton={false}
          />
        </div>
      </ScrollArea>

      <div className="mt-auto flex shrink-0 justify-between border-t border-border bg-card px-4 mb-2 pt-3">
        <Button type="button" onClick={handleCancel} variant="outline" data-testid="button-cancel">
          Cancel
        </Button>
        <FormSubmitButton formId="system-prompt-form">Save</FormSubmitButton>
      </div>
    </div>
  )
}
